import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { addDays } from 'date-fns';
import { useIntl } from '@grandvisionhq/state';
import { DateUtils } from '@grandvisionhq/utils';
import { initialAuthenticatedForm } from '../../../constants/initial-form.js';
import '@grandvisionhq/common';
import '@grandvisionhq/graphql';
import '../../../operations/mutations/cancel-anonymous-subscription.js';
import '../../../operations/mutations/cancel-subscription.js';
import { useCancelSubscription } from '../../../hooks/api/cancelSubscription/use-cancel-subscription.js';
import { useCancellationReason } from '../hooks/use-cancellation-reason.js';
import { AuthenticatedCancelSubscriptionForm } from './AuthenticatedCancelSubscriptionForm.js';

const CANCELLATION_OPTIONS = 4;
const AuthenticatedCancelSubscriptionContainer = ({ subscriptionId , nextPossibleTerminationDate , subscriptionInterval  })=>{
    const formRef = React.useRef(null);
    const { dateTime , getFormattedLabel , getLabel  } = useIntl();
    const [isSuccess, setIsSuccess] = React.useState(false);
    const handleOnCompleted = React.useCallback(()=>{
        var ref;
        setIsSuccess(true);
        (ref = formRef.current) === null || ref === void 0 ? void 0 : ref.resetForm();
        globalThis.window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);
    const { isUpdating , cancelSubscription  } = useCancelSubscription({
        onCompleted: handleOnCompleted
    });
    const { selectedCancellationType , handleFormChange , getReasonFromForm  } = useCancellationReason();
    const handleSubmit = React.useCallback(({ formState  })=>{
        var ref;
        const cancellationDate = (ref = formState.cancellationDate) === null || ref === void 0 ? void 0 : ref.value;
        const reason = getReasonFromForm(formState);
        setIsSuccess(false);
        if (cancellationDate && reason) {
            cancelSubscription(subscriptionId, new Date(cancellationDate), reason);
        }
    }, [
        cancelSubscription,
        getReasonFromForm,
        subscriptionId
    ]);
    const cancellationOptions = React.useMemo(()=>{
        if (!nextPossibleTerminationDate || !subscriptionInterval) {
            return {};
        }
        const terminationDates = [];
        for(let i = 0; i < CANCELLATION_OPTIONS; i++){
            terminationDates.push(addDays(nextPossibleTerminationDate, i * subscriptionInterval));
        }
        return terminationDates.reduce((acc, date)=>{
            const formattedDate = dateTime.formatShortDate(date);
            acc[formattedDate] = {
                label: formattedDate,
                value: DateUtils.toLocalDate(date)
            };
            return acc;
        }, {});
    }, [
        nextPossibleTerminationDate,
        subscriptionInterval,
        dateTime
    ]);
    const initialState = React.useMemo(()=>swcHelpers.objectSpread({}, initialAuthenticatedForm, {
            subscriptionNumber: {
                name: 'subscriptionNumber',
                value: subscriptionId,
                isValid: true,
                isTouched: true
            }
        })
    , [
        subscriptionId
    ]);
    const formLabels = {
        success: {
            title: getFormattedLabel('cancelSubscription.success.title', {
                labelData: {
                    subscriptionNumber: subscriptionId
                }
            }),
            description: getFormattedLabel('cancelSubscription.success.description.authenticated')
        },
        subscriptionNumber: {
            label: getLabel('cancelSubscription.field.subscriptionNumber')
        },
        cancellationDate: {
            label: getLabel('cancelSubscription.field.cancellationDate'),
            placeholder: dateTime.formatMidsizeDate(new Date()),
            errorMessage: getLabel('cancelSubscription.field.cancellationDate.errorMessage')
        },
        submit: getLabel('cancelSubscription.action.submit'),
        formAriaLabel: getLabel('cancelSubscription.title')
    };
    // Determine if the date picker should be shown instead of calculated options
    const shouldShowDatePicker = !nextPossibleTerminationDate;
    return /*#__PURE__*/ React.createElement(AuthenticatedCancelSubscriptionForm, {
        formRef: formRef,
        initialState: initialState,
        isSuccess: isSuccess,
        isUpdating: isUpdating,
        onBlur: handleFormChange,
        onChange: handleFormChange,
        onSubmit: handleSubmit,
        selectedCancellationType: selectedCancellationType,
        cancellationOptions: cancellationOptions,
        labels: formLabels,
        shouldShowDatePicker: shouldShowDatePicker
    });
};

export { AuthenticatedCancelSubscriptionContainer };
//# sourceMappingURL=AuthenticatedCancelSubscriptionContainer.js.map
